// In-App Purchase service — Capacitor + cordova-plugin-purchase v13.
//
// On mobile (iOS/Android built via Capacitor), we surface the native
// StoreKit / Play Billing UI. On the web (PWA / preview environment) all
// helpers become no-ops and callers should fall back to the Razorpay flow.
//
// Docs: https://github.com/j3k0/cordova-plugin-purchase
//
// Product SKUs must match the ones registered in App Store Connect and
// Google Play Console, and mirror /api/iap/catalog on the backend.

import api from './api';

/** Store product identifiers. Must match backend IAP_CATALOG. */
export const IAP_SKUS = {
  starter:    'com.worldvibe.stars.starter',
  explorer:   'com.worldvibe.stars.explorer',
  voyager:    'com.worldvibe.stars.voyager',
  cosmonaut:  'com.worldvibe.stars.cosmonaut',
  galactic:   'com.worldvibe.stars.galactic',
  supreme:    'com.worldvibe.stars.supreme',
  vipMonthly: 'com.worldvibe.vip.monthly',
};

/** True when running inside a Capacitor-built native shell (iOS / Android). */
export function isNativeMobile() {
  const w = typeof window !== 'undefined' ? window : {};
  const cap = w.Capacitor;
  if (!cap || typeof cap.isNativePlatform !== 'function') return false;
  return cap.isNativePlatform();
}

/** Platform string: 'web' | 'ios' | 'android'. */
export function nativePlatform() {
  const w = typeof window !== 'undefined' ? window : {};
  const cap = w.Capacitor;
  if (cap && typeof cap.getPlatform === 'function') return cap.getPlatform();
  return 'web';
}

// ---------------------------------------------------------------------------
// cordova-plugin-purchase lifecycle
// ---------------------------------------------------------------------------
let _store = null;
let _initPromise = null;

/**
 * Register products and initialise the store. Safe to call multiple times —
 * repeated calls short-circuit to the first initialisation promise.
 *
 * Resolves with the CDVPurchase.Store instance so callers can inspect
 * product prices, availability, etc.
 */
export async function initIap() {
  if (!isNativeMobile()) return null;
  if (_initPromise) return _initPromise;

  _initPromise = (async () => {
    // The plugin exposes CDVPurchase on the global scope once cordova is ready.
    const CDVPurchase = typeof window !== 'undefined' ? window.CDVPurchase : null;
    if (!CDVPurchase) {
      console.warn('[iap] cordova-plugin-purchase not available — skipping init');
      return null;
    }
    const store = CDVPurchase.store;
    _store = store;

    const platform = nativePlatform() === 'ios'
      ? CDVPurchase.Platform.APPLE_APPSTORE
      : CDVPurchase.Platform.GOOGLE_PLAY;

    const products = [
      { id: IAP_SKUS.starter,    type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.explorer,   type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.voyager,    type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.cosmonaut,  type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.galactic,   type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.supreme,    type: CDVPurchase.ProductType.CONSUMABLE, platform },
      { id: IAP_SKUS.vipMonthly, type: CDVPurchase.ProductType.PAID_SUBSCRIPTION, platform },
    ];

    store.register(products);

    store.when()
      .approved(async (transaction) => {
        try {
          await _verifyOnBackend(transaction);
          transaction.finish();
        } catch (err) {
          console.error('[iap] verify failed', err);
        }
      })
      .verified((receipt) => receipt.finish?.())
      .productUpdated((p) => {
        console.log('[iap] product', p.id, p.priceMicros, p.canPurchase);
      })
      .error((err) => {
        console.error('[iap] store error', err);
      });

    await store.initialize([platform]);
    return store;
  })();

  return _initPromise;
}

async function _verifyOnBackend(transaction) {
  const platform = nativePlatform();
  if (platform === 'ios') {
    // StoreKit v1: transaction.transactionReceipt is the base64 receipt
    // StoreKit v2 via plugin: transaction.nativePurchase.transactionReceipt
    const receipt = transaction.nativePurchase?.appStoreReceipt
      || transaction.transactionReceipt
      || (transaction.parentReceipt && transaction.parentReceipt.nativeData?.appStoreReceipt)
      || '';
    if (!receipt) throw new Error('Missing App Store receipt');
    const productId = transaction.products?.[0]?.id || transaction.productId;
    const { data } = await api.post('/iap/apple/verify', { receipt, product_id: productId });
    return data;
  }
  if (platform === 'android') {
    const productId = transaction.products?.[0]?.id || transaction.productId;
    const purchaseToken = transaction.nativePurchase?.purchaseToken || transaction.purchaseToken;
    const kind = productId === IAP_SKUS.vipMonthly ? 'subscription' : 'consumable';
    if (!purchaseToken) throw new Error('Missing Play Billing token');
    const { data } = await api.post('/iap/google/verify', { product_id: productId, purchase_token: purchaseToken, kind });
    return data;
  }
  throw new Error(`Unsupported platform: ${platform}`);
}

/** Start the checkout flow for one SKU. Returns a promise that resolves
 *  when the purchase either completes (approved+verified+finished) or is
 *  cancelled/errors out. */
export async function purchase(sku) {
  if (!isNativeMobile()) throw new Error('IAP is only available on the native app');
  const store = await initIap();
  if (!store) throw new Error('Store not initialised');
  const product = store.get(sku);
  if (!product) throw new Error(`Unknown SKU: ${sku}`);
  return store.order(product);
}

/** Restore prior non-consumable / subscription purchases (mandatory on iOS). */
export async function restorePurchases() {
  if (!isNativeMobile()) return null;
  const store = await initIap();
  return store?.restorePurchases();
}

/** Get the storefront price string ("$0.99") for a SKU, or null on web. */
export function priceFor(sku) {
  if (!_store) return null;
  const p = _store.get(sku);
  return p?.pricing?.price || p?.price || null;
}
